import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Inbox, Plus, Trash2, Pencil, RefreshCw, MailOpen, Send } from 'lucide-react'
import { getMailboxes, createMailbox, updateMailbox, deleteMailbox, checkMailbox, getEmails, deleteEmail, sendMailboxTest, type MailboxInput } from '../api/mail'
import { getDatasets } from '../api/storage'
import { Badge } from '../components/Badge'
import { Modal } from '../components/Modal'
import { EmptyState } from '../components/EmptyState'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { SearchInput } from '../components/SearchInput'
import { useDateFmt } from '../hooks/useDateFmt'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import { Select, SelectItem } from '../components/ui/select'
import type { Mailbox, Email } from '../types'

const empty = {
  name: '', host: '', port: '993', user: '', password: '', tls: true, storageDatasetId: '', isActive: true,
  smtpEnabled: false, smtpHost: '', smtpPort: '587', smtpSecure: false,
  smtpOwnLogin: false, smtpUser: '', smtpPassword: '', smtpFromName: '', smtpFromAddress: '',
}
type MailboxForm = typeof empty

// What the form sends: blank passwords are left out (keep the stored one), and a separate
// sending login is only sent when "use a different login" is ticked (cleared otherwise).
function toInput(f: MailboxForm): MailboxInput {
  return {
    name: f.name, host: f.host, port: parseInt(f.port), user: f.user, tls: f.tls, isActive: f.isActive,
    storageDatasetId: f.storageDatasetId || null,
    ...(f.password ? { password: f.password } : {}),
    smtpEnabled: f.smtpEnabled, smtpHost: f.smtpHost || null, smtpPort: f.smtpPort ? parseInt(f.smtpPort) : null, smtpSecure: f.smtpSecure,
    smtpUser: f.smtpOwnLogin ? (f.smtpUser || null) : null,
    ...(f.smtpOwnLogin ? (f.smtpPassword ? { smtpPassword: f.smtpPassword } : {}) : { smtpPassword: null }),
    smtpFromName: f.smtpFromName || null, smtpFromAddress: f.smtpFromAddress || null,
  }
}

export function Mailboxes() {
  const fmt = useDateFmt()
  const qc = useQueryClient()

  const { data: mailboxes = [], isLoading } = useQuery({
    queryKey: ['mailboxes'],
    queryFn: () => getMailboxes().then((r) => r.data),
  })

  const { data: datasets = [] } = useQuery({
    queryKey: ['storage-datasets'], queryFn: () => getDatasets().then(r => r.data),
  })

  // Create / Edit
  const [editTarget, setEditTarget] = useState<Mailbox | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState<MailboxForm>(empty)
  const [testTo, setTestTo] = useState('')

  const openCreate = () => { setForm(empty); setShowCreate(true) }
  const openEdit = (mb: Mailbox) => {
    setForm({
      name: mb.name, host: mb.host, port: String(mb.port), user: mb.user, password: '', tls: mb.tls,
      storageDatasetId: mb.storageDatasetId ?? '', isActive: mb.isActive,
      smtpEnabled: mb.smtpEnabled ?? false, smtpHost: mb.smtpHost ?? '', smtpPort: mb.smtpPort ? String(mb.smtpPort) : '587',
      smtpSecure: mb.smtpSecure ?? false, smtpOwnLogin: !!mb.smtpUser || !!mb.smtpPasswordSet,
      smtpUser: mb.smtpUser ?? '', smtpPassword: '', smtpFromName: mb.smtpFromName ?? '', smtpFromAddress: mb.smtpFromAddress ?? '',
    })
    setTestTo('')
    setEditTarget(mb)
  }
  const closeForm = () => { setShowCreate(false); setEditTarget(null); test.reset() }

  const CHECKBOXES = new Set<keyof MailboxForm>(['tls', 'isActive', 'smtpEnabled', 'smtpSecure', 'smtpOwnLogin'])
  const setF = (k: keyof MailboxForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: CHECKBOXES.has(k) ? (e.target as HTMLInputElement).checked : e.target.value }))

  const create = useMutation({
    mutationFn: () => createMailbox(toInput(form)),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['mailboxes'] }); closeForm() },
  })

  const update = useMutation({
    mutationFn: () => updateMailbox(editTarget!.id, toInput(form)),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['mailboxes'] }); closeForm() },
  })

  const test = useMutation({
    mutationFn: () => sendMailboxTest(editTarget!.id, testTo),
  })

  const [deleteTarget, setDeleteTarget] = useState<Mailbox | null>(null)
  const del = useMutation({
    mutationFn: (id: string) => deleteMailbox(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['mailboxes'] }); setDeleteTarget(null) },
  })

  const check = useMutation({
    mutationFn: (id: string) => checkMailbox(id),
  })

  // Emails drawer
  const [emailsMailbox, setEmailsMailbox] = useState<Mailbox | null>(null)
  const { data: emails = [], isLoading: emailsLoading } = useQuery({
    queryKey: ['emails', emailsMailbox?.id],
    queryFn: () => emailsMailbox ? getEmails({ mailboxId: emailsMailbox.id }).then((r) => r.data) : [],
    enabled: !!emailsMailbox,
  })

  const [deleteEmailTarget, setDeleteEmailTarget] = useState<Email | null>(null)
  const delEmail = useMutation({
    mutationFn: (id: string) => deleteEmail(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['emails', emailsMailbox?.id] }); setDeleteEmailTarget(null) },
  })

  const [search, setSearch] = useState('')
  const filteredMailboxes = mailboxes.filter((mb: Mailbox) =>
    [mb.name, mb.host, mb.user].some((v) => v.toLowerCase().includes(search.toLowerCase()))
  )

  const showForm = showCreate || !!editTarget
  const formTitle = editTarget ? `Edit  ${editTarget.name}` : 'New mailbox'
  const onSubmit = () => editTarget ? update.mutate() : create.mutate()
  const isPending = create.isPending || update.isPending

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Mailboxes</h1>
          <p className="text-sm text-ink-dim mt-0.5">Mailboxes that receive (IMAP) and send (SMTP) email</p>
        </div>
        <div className="flex items-center gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder="Search mailboxes" />
          <Button icon={Plus} onClick={openCreate}>Add mailbox</Button>
        </div>
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-ink-dim">Loading</div>
        ) : mailboxes.length === 0 ? (
          <EmptyState icon={Inbox} title="No mailboxes" description="Add an IMAP mailbox to start monitoring" />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Host</th>
                <th>User</th>
                <th>TLS</th>
                <th>Incoming</th>
                <th>Sending</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filteredMailboxes.map((mb: Mailbox) => (
                <tr key={mb.id}>
                  <td className="font-medium text-ink">{mb.name}</td>
                  <td className="text-ink-dim">{mb.host}:{mb.port}</td>
                  <td className="text-ink-dim">{mb.user}</td>
                  <td>
                    <Badge variant={mb.tls ? 'success' : 'default'}>{mb.tls ? 'TLS' : 'Plain'}</Badge>
                  </td>
                  <td>
                    <Badge variant={mb.isActive ? 'success' : 'default'}>{mb.isActive ? 'Monitored' : 'Not monitored'}</Badge>
                  </td>
                  <td>
                    <Badge variant={mb.smtpEnabled ? 'success' : 'default'}>{mb.smtpEnabled ? 'Enabled' : 'Off'}</Badge>
                  </td>
                  <td>
                    <div className="flex items-center gap-3 justify-end">
                      <button onClick={() => setEmailsMailbox(mb)} className="flex items-center gap-1 text-xs text-ink-dim hover:text-ink font-medium" title="View emails">
                        <MailOpen size={13} /> Emails
                      </button>
                      <button onClick={() => check.mutate(mb.id)} disabled={!mb.isActive || check.isPending} className="text-ink-dim hover:text-ink disabled:opacity-40 transition-colors" title="Manual check">
                        <RefreshCw size={13} />
                      </button>
                      <button onClick={() => openEdit(mb)} className="text-ink-dim hover:text-ink transition-colors" title="Edit">
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => setDeleteTarget(mb)} className="text-ink-dim hover:text-red-500 transition-colors" title="Delete">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create / Edit modal */}
      {showForm && (
        <Modal title={formTitle} onClose={closeForm} size="md">
          <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
            <p className="text-sm font-semibold text-gray-800">Receiving (IMAP)</p>
            {[
              { key: 'name', label: 'Name *', placeholder: 'support-inbox', type: 'text' },
              { key: 'host', label: 'IMAP host *', placeholder: 'imap.gmail.com', type: 'text' },
              { key: 'port', label: 'Port *', placeholder: '993', type: 'number' },
              { key: 'user', label: 'Username *', placeholder: 'user@company.com', type: 'text' },
              { key: 'password', label: editTarget ? 'Password (leave blank to keep)' : 'Password *', placeholder: '••••••••', type: 'password' },
            ].map(({ key, label, placeholder, type }) => (
              <Field key={key} label={label}>
                <input value={String(form[key as keyof typeof form])} onChange={setF(key as keyof typeof empty)} placeholder={placeholder} type={type}
                  className="field" />
              </Field>
            ))}
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.tls} onChange={setF('tls')} className="rounded border-paper-border" />
              <span className="text-sm text-ink">Enable TLS</span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={form.isActive} onChange={setF('isActive')} className="mt-0.5 rounded border-paper-border" />
              <span className="text-sm text-ink">
                Monitor incoming email
                <span className="block text-xs text-ink-dim">Checks this mailbox for new emails. Turn off to keep it for sending only.</span>
              </span>
            </label>
            <Field label="Destination dataset *">
              <Select
                value={form.storageDatasetId}
                onValueChange={(v) => setForm((f) => ({ ...f, storageDatasetId: v }))}
                placeholder="Select a dataset for attachments..."
              >
                {datasets.map(d => (
                  <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>
                ))}
              </Select>
            </Field>
            <div className="space-y-4 border-t border-gray-100 pt-4">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={form.smtpEnabled} onChange={setF('smtpEnabled')} className="mt-0.5 rounded border-paper-border" />
                <span className="text-sm font-semibold text-gray-800">
                  Send email from this mailbox (SMTP)
                  <span className="block text-xs font-normal text-ink-dim">Used e.g. by a shop to email quotes to clients.</span>
                </span>
              </label>
              {form.smtpEnabled && (
                <>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_7rem]">
                    <Field label="SMTP host *">
                      <input value={form.smtpHost} onChange={setF('smtpHost')} placeholder="smtp.gmail.com" className="field" />
                    </Field>
                    <Field label="Port">
                      <input value={form.smtpPort} onChange={setF('smtpPort')} placeholder="587" type="number" className="field" />
                    </Field>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.smtpSecure} onChange={setF('smtpSecure')} className="rounded border-paper-border" />
                    <span className="text-sm text-ink">SSL from the start (usually port 465; leave off for 587 / STARTTLS)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.smtpOwnLogin} onChange={setF('smtpOwnLogin')} className="rounded border-paper-border" />
                    <span className="text-sm text-ink">Use a different login for sending (default: the receiving username and password)</span>
                  </label>
                  {form.smtpOwnLogin && (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field label="SMTP username">
                        <input value={form.smtpUser} onChange={setF('smtpUser')} placeholder={form.user} className="field" />
                      </Field>
                      <Field label={editTarget?.smtpPasswordSet ? 'SMTP password (leave blank to keep)' : 'SMTP password'}>
                        <input value={form.smtpPassword} onChange={setF('smtpPassword')} type="password" placeholder="••••••••" className="field" />
                      </Field>
                    </div>
                  )}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Sender name">
                      <input value={form.smtpFromName} onChange={setF('smtpFromName')} placeholder="Garage Dupont" className="field" />
                    </Field>
                    <Field label="Sender address">
                      <input value={form.smtpFromAddress} onChange={setF('smtpFromAddress')} placeholder={form.user || 'quotes@company.com'} className="field" />
                    </Field>
                  </div>
                  {editTarget && (
                    <div className="rounded-lg border border-gray-200 p-3 space-y-2">
                      <p className="text-xs text-ink-dim">Send a test email with the saved settings (save your changes first).</p>
                      <div className="flex gap-2">
                        <input value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="you@company.com" className="field flex-1" />
                        <Button variant="secondary" icon={Send} onClick={() => test.mutate()} disabled={!testTo} loading={test.isPending}>Send test</Button>
                      </div>
                      {test.isSuccess && <p className="text-xs text-success-600">Test email sent.</p>}
                      {test.isError && <p className="text-xs text-error-500">{(test.error as any)?.response?.data?.error ?? 'Sending failed'}</p>}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={closeForm}>Cancel</Button>
              <Button
                onClick={onSubmit}
                disabled={!form.name || !form.host || !form.port || !form.user || !form.storageDatasetId || (!editTarget && !form.password) || (form.smtpEnabled && !form.smtpHost)}
                loading={isPending}
              >
                {editTarget ? 'Save' : 'Add'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Emails modal */}
      {emailsMailbox && (
        <Modal title={`Emails  ${emailsMailbox.name}`} onClose={() => setEmailsMailbox(null)} size="lg">
          {emailsLoading ? (
            <div className="text-sm text-ink-dim text-center py-4">Loading</div>
          ) : emails.length === 0 ? (
            <p className="text-sm text-ink-dim text-center py-4">No emails captured yet</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto scrollbar-thin">
              {emails.map((e: Email) => (
                <div key={e.id} className={`px-4 py-3 rounded-lg border ${e.isRead ? 'border-paper-border bg-paper-sunken' : 'border-paper-border bg-white'}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className={`text-sm truncate ${e.isRead ? 'text-ink-dim' : 'font-medium text-ink'}`}>{e.subject || '(no subject)'}</p>
                      <p className="text-xs text-ink-dim mt-0.5">From: {e.sender}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-ink-dim">{fmt(e.receivedTime)}</span>
                      <button
                        onClick={() => setDeleteEmailTarget(e)}
                        className="text-ink-dim hover:text-red-500 transition-colors"
                        title="Delete email"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete mailbox"
          message={`Delete "${deleteTarget.name}"? Monitoring will stop and all captured emails will be removed.`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => del.mutate(deleteTarget.id)}
          loading={del.isPending}
        />
      )}

      {deleteEmailTarget && (
        <ConfirmDialog
          title="Delete email"
          message={`Delete "${deleteEmailTarget.subject || '(no subject)'}"? This cannot be undone.`}
          onCancel={() => setDeleteEmailTarget(null)}
          onConfirm={() => delEmail.mutate(deleteEmailTarget.id)}
          loading={delEmail.isPending}
        />
      )}
    </div>
  )
}
